import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, Loader2, User, Phone, IdCard, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminGlobalSearch — small debounced search box that queries
 * `GET /api/admin/search?q=...` and opens the user dossier on click.
 * Matches by: name, email, phone, visible_id, CURP, clave de elector,
 * public IP, private IP, device fingerprint.
 */
export const AdminGlobalSearch = ({ authHeader, onOpenDossier }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setLoading(true);
        const r = await axios.get(`${API}/admin/search`, {
          params: { q: q.trim() },
          headers: authHeader(),
          signal: abortRef.current.signal,
        });
        setResults(r.data.results || []);
        setOpen(true);
      } catch (_) { /* aborted or network */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, authHeader]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={boxRef} className="relative w-full sm:max-w-md" data-testid="admin-global-search-box">
      <div className="relative">
        <Search className="w-4 h-4 text-[#d4af37]/70 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          data-testid="admin-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.length >= 2 && setOpen(true)}
          placeholder="Buscar por nombre, email, teléfono, ID, CURP, IP…"
          className="w-full pl-9 pr-9 py-2.5 rounded-full bg-black/60 border border-[#d4af37]/30 text-white text-sm placeholder-neutral-500 focus:border-[#d4af37]/70 focus:outline-none"
        />
        {loading && <Loader2 className="w-4 h-4 text-neutral-400 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />}
      </div>
      {open && (results.length > 0 || (q.length >= 2 && !loading)) && (
        <div className="absolute z-50 mt-1 w-full bg-[#0a0510] border border-[#d4af37]/30 rounded-xl shadow-2xl max-h-96 overflow-y-auto" data-testid="admin-search-results">
          {results.length === 0 ? (
            <div className="p-3 text-xs text-neutral-500 italic">Sin coincidencias para <b className="text-white">{q}</b></div>
          ) : (
            results.map((r) => (
              <button
                key={r.id}
                data-testid={`admin-search-result-${r.visible_id || r.id}`}
                onClick={() => { setOpen(false); onOpenDossier?.(r); }}
                className="w-full text-left px-3 py-2.5 hover:bg-[#d4af37]/10 border-b border-white/5 last:border-0 flex items-start gap-2"
              >
                <User className="w-4 h-4 text-[#d4af37] mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">
                    {r.name || r.username || "(sin nombre)"}
                    {r.visible_id ? <span className="ml-2 text-[10px] font-mono text-[#d4af37]">{r.visible_id}</span> : null}
                  </div>
                  <div className="text-[10px] text-neutral-400 flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                    {r.email && <span className="flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />{r.email}</span>}
                    {r.phone && <span className="flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{r.phone}</span>}
                    {r.country && <span>{r.country}</span>}
                  </div>
                  {r.matched_on?.length > 0 && (
                    <div className="text-[9px] text-neutral-500 mt-0.5 flex flex-wrap gap-1">
                      {r.matched_on.map((m) => (
                        <span key={m} className="px-1.5 py-0.5 rounded-full border border-[#d4af37]/20 bg-[#d4af37]/5 text-[#d4af37]/80">
                          {m}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <IdCard className="w-3.5 h-3.5 text-neutral-500 mt-1" />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdminGlobalSearch;
